import React from "react";
export const SITE_URL = (process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export const SITE_NAME = "Dra. Cometa";
export const SOCIALS = [
  "https://www.youtube.com/@dracometa",
  "https://www.instagram.com/doctora_cometa",
  "https://www.tiktok.com/@dra..cometa",
  "https://www.facebook.com/profile.php?id=61589307333290",
];
export const TELEFONO = "+52 55 2898 0641";
export const KEYWORDS_BASE = [
  "Dra. Cometa", "Doctora Cometa", "Araceli Lugo", "podcast de bienestar", "podcast de liderazgo con propósito",
  "comunidad adultos mayores", "experiencias para adultos mayores México", "actividades para adultos mayores CDMX",
  "Utopistas", "círculos de lectura adultos mayores", "Ciudad de México",
];

const origin = () => (process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");

export const SEO = ({ title, description, image = "/og-image.jpg", type = "website", jsonLd, noindex = false, keywords = [], publishedTime, modifiedTime }) => {
  const { pathname } = useLocation();
  const base = origin();
  const url = `${base}${pathname === "/" ? "" : pathname}`;
  const raw = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const fullTitle = raw.length > 65 ? `${title.slice(0, 50).trim()}… | ${SITE_NAME}` : raw;
  const img = image.startsWith("http") ? image : `${base}${image}`;
  const kw = [...new Set([...keywords, ...KEYWORDS_BASE])].join(", ");
  const lds = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  return (
    <Helmet>
      <html lang="es-MX" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={kw} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="geo.region" content="MX-CMX" />
      <meta name="geo.placename" content="Ciudad de México" />
      <meta name="geo.position" content="19.4326;-99.1332" />
      <meta name="ICBM" content="19.4326, -99.1332" />
      <link rel="canonical" href={url || "/"} />
      <link rel="alternate" hrefLang="es-MX" href={url || "/"} />
      <link rel="alternate" hrefLang="x-default" href={url || "/"} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_MX" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={img} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {type === "article" && <meta property="article:author" content="Araceli Lugo" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {lds.map((ld, i) => <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>)}
    </Helmet>
  );
};

export const breadcrumbJsonLd = (items) => {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{ name: "Inicio", path: "/" }, ...items].map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${base}${it.path === "/" ? "" : it.path}` || base })),
  };
};

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const podcastJsonLd = (videos = []) => {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@type": "PodcastSeries",
    "@id": `${base}/podcast#series`,
    name: "Dra. Cometa y su Utopía",
    description: "Podcast de liderazgo con propósito, bienestar y transformación social conducido por Araceli Lugo, Dra. Cometa.",
    url: `${base}/podcast`,
    inLanguage: "es-MX",
    webFeed: "https://www.youtube.com/feeds/videos.xml?channel_id=UCsgZqab23nvwyvPMU_24UAw",
    author: { "@id": `${base}/#araceli` },
    sameAs: "https://www.youtube.com/@dracometa",
    hasPart: videos.slice(0, 10).map((v) => ({
      "@type": "PodcastEpisode",
      name: v.title,
      url: v.url,
      datePublished: v.published || undefined,
      thumbnailUrl: v.thumbnail,
      partOfSeries: { "@id": `${base}/podcast#series` },
      associatedMedia: { "@type": "VideoObject", name: v.title, thumbnailUrl: v.thumbnail, embedUrl: `https://www.youtube.com/embed/${v.id}`, uploadDate: v.published || undefined },
    })),
  };
};

export const orgJsonLd = () => {
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "LocalBusiness"],
        "@id": `${base}/#org`,
        name: "Dra. Cometa",
        alternateName: ["Doctora Cometa", "Cometa", "Utopistas by Dra. Cometa"],
        url: base,
        logo: `${base}/icon-512.png`,
        image: `${base}/og-image.jpg`,
        description: "Movimiento de Araceli Lugo para acercar realidades y activar posibilidades: podcast de bienestar y liderazgo con propósito, Revista Cometa, Constelación de Utopías y Utopistas, comunidad de experiencias para adultos mayores en Ciudad de México.",
        email: "contacto@dracometa.com.mx",
        telephone: TELEFONO,
        address: { "@type": "PostalAddress", addressLocality: "Ciudad de México", addressRegion: "CDMX", addressCountry: "MX" },
        geo: { "@type": "GeoCoordinates", latitude: 19.4326, longitude: -99.1332 },
        areaServed: [{ "@type": "City", name: "Ciudad de México" }, { "@type": "Country", name: "México" }],
        knowsLanguage: "es-MX",
        sameAs: SOCIALS,
        founder: { "@id": `${base}/#araceli` },
        contactPoint: { "@type": "ContactPoint", telephone: TELEFONO, contactType: "customer service", availableLanguage: "Spanish", areaServed: "MX" },
      },
      {
        "@type": "Person",
        "@id": `${base}/#araceli`,
        name: "Araceli Lugo",
        alternateName: ["Dra. Cometa", "Doctora Cometa"],
        jobTitle: "Abogada, fundadora de Cometa y conductora del podcast Dra. Cometa y su Utopía",
        knowsAbout: ["Liderazgo con propósito", "Impacto social", "Bienestar", "Finanzas corporativas", "Adultos mayores", "Comunidad"],
        url: base,
        image: `${base}/img/ara_bn.webp`,
        sameAs: SOCIALS,
        worksFor: { "@id": `${base}/#org` },
        address: { "@type": "PostalAddress", addressLocality: "Ciudad de México", addressCountry: "MX" },
      },
      {
        "@type": "WebSite",
        "@id": `${base}/#website`,
        url: base,
        name: "Dra. Cometa",
        inLanguage: "es-MX",
        publisher: { "@id": `${base}/#org` },
        potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${base}/blog?q={search_term_string}` }, "query-input": "required name=search_term_string" },
      },
      {
        "@type": "Organization",
        "@id": `${base}/marca#utopistas`,
        name: "Utopistas",
        alternateName: "Utopistas by Dra. Cometa",
        url: `${base}/marca`,
        parentOrganization: { "@id": `${base}/#org` },
        description: "Comunidad de experiencias para adultos mayores en Ciudad de México: Tertulia, Letras y Lenguas, Tercera Llamada, Experiencias en familia, Vivir Pleno y Futuro Seguro.",
        areaServed: { "@type": "City", name: "Ciudad de México" },
        audience: { "@type": "PeopleAudience", suggestedMinAge: 60 },
      },
    ],
  };
};
